/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Liste des services du site UK.
 *
 * Le catalogue vient des traductions ; chaque entree porte un `slug` qui sert a
 * la fois de cle de route (`/services/:slug`) et de cle d'image dans
 * `SERVICE_IMAGES`. Ajouter un service demande donc de renseigner les quatre
 * fichiers de langue ET `lib/media.js`.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import { SERVICE_IMAGES } from "../lib/media";
import { ArrowRight } from "lucide-react";

const Services = () => {
  const { t, lang } = useLang();
  if (!t) return null;
  const meta = t.meta.pages.services;
  const services = t.services.items;

  return (
    <main data-testid="services-page" className="pt-20">
      <SEO title={meta.title} description={meta.description} lang={lang} />

      <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-24 lg:py-32">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
            {t.services.intro.eyebrow}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            {t.services.intro.title}
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">{t.services.intro.subtitle}</p>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`services-card-${s.slug}`}
                className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden card-lift"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={SERVICE_IMAGES[s.slug]}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />
                </div>
                <div className="flex flex-col flex-1 p-7">
                  <h3 className="text-2xl font-bold text-slate-950 mb-3">{s.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-6 flex-1">{s.short}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                    {t.common.learn_more}
                    <ArrowRight className="h-4 w-4 rtl-flip" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-950">{t.home.cta_band.title}</h2>
          <p className="mt-3 text-slate-600 text-lg">{t.home.cta_band.subtitle}</p>
          <Link
            to="/contact"
            data-testid="services-bottom-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-950 hover:bg-blue-600 text-white font-semibold px-7 py-3.5 transition-colors"
          >
            {t.home.cta_band.cta}
            <ArrowRight className="h-4 w-4 rtl-flip" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Services;
